import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../features/auth/context/AuthContext';
import { createFeatureSwitchService } from '../../features/kernel/services/featureSwitchService';
import type { FeatureSwitchesView, SwitchName } from '../../types/backend';

// One cache entry per signed-in user and view-as role, since switches resolve from the caller's
// groups: every switch read shares the one ops.GetFeatureSwitches call, a second user on the same
// page never reads the first user's answer, and the Kernel panel's changes invalidate them all.
export const featureSwitchKeys = {
  all: () => ['featureSwitches'] as const,
  caller: (sub: string | undefined, viewAsRole: string | null) => ['featureSwitches', sub, viewAsRole] as const,
};

export interface FeatureSwitchesQuery {
  view: FeatureSwitchesView | undefined;
  error: Error | null;
  loading: boolean;
}

export function useFeatureSwitchesView(): FeatureSwitchesQuery {
  const { idToken, user, viewAsRole } = useAuth();
  const query = useQuery({
    queryKey: featureSwitchKeys.caller(user?.sub, viewAsRole),
    queryFn: () => createFeatureSwitchService(idToken as string).getFeatureSwitches(),
    enabled: !!idToken,
  });
  return { view: query.data, error: query.error, loading: query.isFetching };
}

/**
 * Whether `name` is on for the caller, as the server resolved it from the caller's staff groups
 * (ops.GetFeatureSwitches). Read it only at a feature's entry points: a route, a navigation entry,
 * a menu item.
 *
 * Off while loading, on a failed read, and for a switch the server did not return: the feature
 * appears only once the server says it is on.
 */
export function useFeatureSwitch(name: SwitchName): boolean {
  const { view, error } = useFeatureSwitchesView();
  if (error || !view) return false;
  return view.switches.some((s) => s.name === name && s.on);
}
